"use client";

import { useState, useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import { AlertTriangle, ExternalLink, X } from "lucide-react";
import { buildInAppEscapeUrl, detectInAppPlatform, isInAppBrowser } from "@/checkout/lib/redirect-capability";

export function InAppBrowserBanner() {
	const t = useTranslations("checkout.inAppBanner");
	const [show, setShow] = useState(false);
	const [showMenuHint, setShowMenuHint] = useState(false);
	const initializedRef = useRef(false);

	useEffect(() => {
		if (initializedRef.current) return;
		initializedRef.current = true;

		const dismissed = document.cookie.includes("dismiss_inapp_banner=1");
		if (dismissed) return;

		// Dieselbe Markerliste, die im Payment-Step ueber Pre-Open oder Same-Tab
		// entscheidet. Getrennte Listen wuerden bedeuten, dass der Banner "im Browser
		// oeffnen" empfiehlt, waehrend der Checkout weiter auf einen zweiten Tab setzt.
		if (isInAppBrowser(navigator.userAgent)) {
			setShow(true);
		}
	}, []);

	if (!show) {
		return null;
	}

	// Kaskade statt Einzelmethode: kein Verfahren deckt alle In-App-Browser ab.
	// Die Reihenfolge haengt an der Plattform, weil window.open nicht ueberall
	// scheitert, wo es nutzlos ist:
	//
	// Android-WebViews mit setSupportMultipleWindows(false), dem Default, geben bei
	// window.open KEIN null zurueck. Sie behandeln den Aufruf als Top-Level-Navigation
	// im selben WebView. Der Button wuerde dort also nur den Checkout neu laden und
	// zurueckkehren, und die intent://-Stufe waere ausgerechnet in der Konfiguration
	// unerreichbar, fuer die sie geschrieben ist. Deshalb auf Android das
	// Plattform-Schema zuerst, ganz ohne window.open.
	//
	// Auf iOS bleibt window.open Stufe 1: in der Google-Such-App funktioniert es, und
	// in WKWebViews liefert es sauber null, sodass x-safari-https:// danach greift.
	// Nie beides zusammen, sonst navigiert die Seite unter einem gerade geoeffneten
	// Fenster weg. Letzte Stufe ist immer der sichtbare Hinweis auf das App-Menue.
	const handleOpenExternal = () => {
		const ua = navigator.userAgent;
		const href = window.location.href;
		const escapeUrl = buildInAppEscapeUrl(ua, href);
		const skipWindowOpen = detectInAppPlatform(ua) === "android" && escapeUrl !== null;

		if (!skipWindowOpen) {
			const opened = window.open(href, "_blank");
			if (opened) return;
		}

		if (escapeUrl) {
			try {
				window.location.href = escapeUrl;
			} catch {
				/* kein Handler fuer das Schema, dann bleibt die letzte Stufe */
			}
		}
		setShowMenuHint(true);
	};

	const handleDismiss = () => {
		document.cookie = "dismiss_inapp_banner=1;path=/;max-age=86400";
		setShow(false);
	};

	return (
		<div className="mx-auto mb-4 flex max-w-lg items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
			<AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
			<div className="flex-1">
				<p>{t("message")}</p>
				<button
					onClick={handleOpenExternal}
					className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-amber-700"
				>
					<ExternalLink className="h-3.5 w-3.5" />
					{t("cta")}
				</button>
				{showMenuHint && (
					<p role="status" className="mt-2 text-xs">
						{t("menuHint")}
					</p>
				)}
			</div>
			<button onClick={handleDismiss} className="shrink-0 text-amber-600 hover:text-amber-800">
				<X className="h-4 w-4" />
			</button>
		</div>
	);
}
